/**
 * People (Core) → Reports.
 *
 * Spec: .claude/specs/reports-v2-shell-and-people-analytics.md (Package 1).
 *
 * Replaces the standalone /reports page for the People-owned reports. The
 * report body is entirely ReportShell's — this page only picks which report and
 * owns the tab strip, so adding a People report is a backend query plus one
 * entry in REPORTS.
 */
import React from "react";

import ReportShell from "../components/ReportShell";
import { ReportName } from "../api";
import { useRouteTab } from "../hooks/useRouteTab";
import { TabPillBar } from "../components/TabPillBar";

type Tab =
  | "headcount" | "movement" | "attrition" | "org"
  | "leave" | "liability" | "absence";

const REPORTS: Record<Tab, { report: ReportName; title: string; subtitle: string; label: string }> = {
  headcount: {
    report: "headcount",
    title: "Headcount",
    subtitle: "How many people you had, as of the end of the period",
    label: "👥 Headcount",
  },
  movement: {
    report: "headcount-movement",
    title: "Headcount Movement",
    subtitle: "Who joined and who left, and the net change",
    label: "📈 Movement",
  },
  attrition: {
    report: "attrition",
    title: "Attrition & Tenure",
    subtitle: "Annualised attrition, and how long people have been here",
    label: "📉 Attrition",
  },
  org: {
    report: "org-structure",
    title: "Org Structure",
    subtitle: "Span of control, and who reports to nobody",
    label: "🏢 Org Structure",
  },
  leave: {
    report: "leave-utilization",
    title: "Leave Utilization",
    subtitle: "Leave allocated against leave taken",
    label: "🌴 Leave Utilization",
  },
  liability: {
    report: "leave-liability",
    title: "Leave Liability",
    subtitle: "Unused leave, valued in money",
    label: "💰 Leave Liability",
  },
  absence: {
    report: "absenteeism",
    title: "Absenteeism",
    subtitle: "Unplanned absence, lateness and regularizations",
    label: "🗓️ Absenteeism",
  },
};

// Tab -> URL segment. Headcount owns the bare /people-reports path so existing
// links keep rendering what they always rendered.
const SEGMENTS: Record<Tab, string> = {
  headcount: "", movement: "movement", attrition: "attrition", org: "org",
  leave: "leave", liability: "liability", absence: "absence",
};

export default function PeopleReportsPage() {
  // The tab lives in the URL so the sidebar can drive and highlight it, the
  // same way the Payroll sub-pages work (spec module-subpage-navs).
  const [tab, setTab] = useRouteTab<Tab>("/people-reports", SEGMENTS, "headcount");
  const current = REPORTS[tab];

  return (
    <ReportShell
      report={current.report}
      title={current.title}
      subtitle={current.subtitle}
      // The house tab strip, not a hand-rolled one. TabPillBar exists because
      // seven free-standing pills wrapped to three rows on a 400px screen and
      // pushed the content down the page — which is exactly this page's shape.
      // Handed to the shell rather than rendered above it, so the page opens on
      // its title instead of on a naked row of pills.
      tabs={
        <TabPillBar
          ariaLabel="Report"
          items={(Object.keys(REPORTS) as Tab[]).map(key => ({ key, label: REPORTS[key].label }))}
          active={tab}
          onSelect={key => setTab(key as Tab)}
          style={{ marginBottom: 20 }}
        />
      }
    />
  );
}
